// src/components/security/SessionsCard.tsx
/**
 * Who holds a live session right now, and the three ways to end one.
 *
 * This is the report and the lever that 03-architecture-review.md §3.2 said this business did not
 * have on 2026-08-20, when the only way to find out who was logged in was to read `t_token` by hand.
 *
 * Three things here are not obvious from the markup:
 *
 * 1. **A row is a LOGIN, not a token.** The endpoint answers token rows and one login mints two of
 *    them; `groupSessions` puts them back together, and "Odhlásiť reláciu" revokes every token in the
 *    group. Revoking one of the two would leave the other alive and the label would be a lie.
 * 2. **A 404 while revoking is success.** It means the row was already gone — expired, or ended from
 *    another tab — and that is exactly what the button was asked to achieve.
 * 3. **Ending one of your OWN sessions signs you out, whether or not it was this one.** The API
 *    deliberately never returns the token string, so a client cannot tell which of its user's rows is
 *    the one it is authenticating with. Guessing wrong and staying on the page would leave the admin
 *    clicking a dead session until the next request signs them out anyway, from somewhere with no
 *    explanation attached. So an own-user row asks for a second, differently-worded click and then
 *    signs out deliberately.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { errText, httpStatus } from '../../api/errors';
import {
    listSessions,
    revokeAllSessions,
    revokeSession,
    revokeUserSessions,
} from '../../api/securityApi';
import { ActiveSession } from '../../security/mfaContract';
import { logout } from '../../reducers/authSlice';
import { AppDispatch, RootState } from '../../store/store';
import LockedCard from './LockedCard';
import {
    SessionGroup,
    formatSessionTime,
    groupSessions,
    revocationOrder,
    tokenSummary,
} from './sessions';
import {
    Banner,
    Card,
    CardTitle,
    Chip,
    Confirm,
    Danger,
    Hint,
    Input,
    Label,
    LinkButton,
    Muted,
    Row,
    Secondary,
    StateCell,
    Table,
    TableWrap,
} from './ui';

const TITLE = 'Aktívne relácie';

/** No diacritics, so it can be typed on any keyboard layout in a hurry. Compared case-insensitively;
 *  the placeholder shows exactly what to type. */
const KILL_PHRASE = 'ODHLASIT';

const LOAD_FAILED = 'Nepodarilo sa načítať zoznam relácií.';
const REVOKE_FAILED = 'Reláciu sa nepodarilo ukončiť.';
const REVOKE_USER_FAILED = 'Relácie používateľa sa nepodarilo ukončiť.';
const KILL_FAILED = 'Nepodarilo sa odhlásiť všetkých.';

const CONFIRM_LABEL = 'Odhlási to aj vás — potvrdiť';

/** Slovak counts the noun with the number: 1 token, 2–4 tokeny, 0 and 5 or more tokenov. */
function tokenCount(count: number): string {
    if (count === 1) {
        return '1 token';
    }
    if (count >= 2 && count <= 4) {
        return count + ' tokeny';
    }
    return count + ' tokenov';
}

interface SessionsCardProps {
    enabled: boolean;
    sessionVerified: boolean;
}

/**
 * The lock check lives in its own component so the loading state below it can use hooks
 * unconditionally: React forbids a hook that only runs on some renders, and an early
 * `return <LockedCard/>` before the `useEffect` would be exactly that.
 */
const SessionsCard: React.FC<SessionsCardProps> = ({ enabled, sessionVerified }) => {
    if (!enabled || !sessionVerified) {
        return <LockedCard title={TITLE} enabled={enabled} />;
    }
    return <UnlockedSessions />;
};

type Notice = { text: string; tone: 'ok' | 'warn' };

const UnlockedSessions: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const currentUser = useSelector((state: RootState) => state.auth.user);

    const [rows, setRows] = useState<ActiveSession[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<Notice | null>(null);
    /** Which action is in flight, as `session:<key>` / `user:<key>` / `all` / `reload`. One at a time
     *  on purpose: two revocations racing each other against the same rows produce a 404 that reads
     *  like a failure. */
    const [busyKey, setBusyKey] = useState<string | null>(null);
    /** The own-user action waiting for its second click, in the same `kind:key` form. */
    const [confirming, setConfirming] = useState<string | null>(null);
    const [killPhrase, setKillPhrase] = useState('');

    const busy = busyKey !== null;
    const killArmed = killPhrase.trim().toUpperCase() === KILL_PHRASE;

    const load = useCallback(async (): Promise<void> => {
        setError(null);
        try {
            const list = await listSessions();
            setRows(list);
        } catch (err) {
            // Never log the error object: it carries the request headers, and those carry the token.
            setError(errText(err, LOAD_FAILED));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const reload = async () => {
        if (busy) {
            return;
        }
        setBusyKey('reload');
        setNotice(null);
        await load();
        setBusyKey(null);
    };

    const isMine = (group: SessionGroup): boolean =>
        currentUser != null && group.userId === currentUser.id;

    /** Ends the whole login. Sequential and in `revocationOrder`, so the caller's own bearer — which
     *  this client cannot identify — is never the first token to die. */
    const revokeGroup = async (group: SessionGroup) => {
        if (busy) {
            return;
        }
        const mine = isMine(group);
        setBusyKey('session:' + group.key);
        setNotice(null);
        setError(null);
        try {
            const ids = revocationOrder(group);
            for (let i = 0; i < ids.length; i++) {
                try {
                    await revokeSession(ids[i]);
                } catch (err) {
                    if (httpStatus(err) !== 404) {
                        throw err;
                    }
                }
            }
            if (mine) {
                dispatch(logout());
                navigate('/');
                return;
            }
            setConfirming(null);
            setNotice({ text: 'Relácia používateľa ' + group.userEmail + ' bola ukončená.', tone: 'ok' });
            await load();
        } catch (err) {
            setError(errText(err, REVOKE_FAILED));
        } finally {
            setBusyKey(null);
        }
    };

    const revokeUser = async (group: SessionGroup) => {
        if (busy) {
            return;
        }
        const mine = isMine(group);
        setBusyKey('user:' + group.key);
        setNotice(null);
        setError(null);
        try {
            const revoked = await revokeUserSessions(group.userId);
            if (mine) {
                dispatch(logout());
                navigate('/');
                return;
            }
            setConfirming(null);
            setNotice({
                text: 'Používateľ ' + group.userEmail + ' bol odhlásený (' + tokenCount(revoked) + ').',
                tone: 'ok',
            });
            await load();
        } catch (err) {
            setError(errText(err, REVOKE_USER_FAILED));
        } finally {
            setBusyKey(null);
        }
    };

    const killAll = async () => {
        if (busy || !killArmed) {
            return;
        }
        setBusyKey('all');
        setNotice(null);
        setError(null);
        try {
            await revokeAllSessions();
            // Including this one — that is the point of the button, not a side effect.
            dispatch(logout());
            navigate('/');
        } catch (err) {
            setError(errText(err, KILL_FAILED));
            setBusyKey(null);
        }
    };

    const groups = groupSessions(rows);

    const renderActions = (group: SessionGroup) => {
        const mine = isMine(group);
        const sessionKey = 'session:' + group.key;
        const userKey = 'user:' + group.key;

        return (
            <Actions>
                <LinkButton
                    type="button"
                    disabled={busy}
                    onClick={() => {
                        if (mine && confirming !== sessionKey) {
                            setConfirming(sessionKey);
                            return;
                        }
                        revokeGroup(group);
                    }}
                >
                    {busyKey === sessionKey
                        ? 'Odhlasujem…'
                        : mine && confirming === sessionKey
                        ? CONFIRM_LABEL
                        : 'Odhlásiť reláciu'}
                </LinkButton>
                <LinkButton
                    type="button"
                    disabled={busy}
                    onClick={() => {
                        if (mine && confirming !== userKey) {
                            setConfirming(userKey);
                            return;
                        }
                        revokeUser(group);
                    }}
                >
                    {busyKey === userKey
                        ? 'Odhlasujem…'
                        : mine && confirming === userKey
                        ? CONFIRM_LABEL
                        : 'Odhlásiť používateľa všade'}
                </LinkButton>
            </Actions>
        );
    };

    return (
        <Card>
            <CardTitle>{TITLE}</CardTitle>

            {error && <Banner>{error}</Banner>}
            {notice && (
                <Banner $ok={notice.tone === 'ok'} $warn={notice.tone === 'warn'}>
                    {notice.text}
                </Banner>
            )}

            <Row style={{ marginBottom: 12 }}>
                <Secondary type="button" disabled={busy} onClick={reload}>
                    {busyKey === 'reload' ? 'Obnovujem…' : 'Obnoviť zoznam'}
                </Secondary>
            </Row>

            <Hint style={{ margin: '0 0 14px' }}>
                Jedno prihlásenie vytvorí dva tokeny — prístupový a obnovovací — a tu ich zobrazujeme
                ako jednu reláciu. Časy sú v čase servera.
            </Hint>

            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Používateľ</th>
                            <th>Prihlásenie</th>
                            <th>Tokeny</th>
                            <th>2FA</th>
                            <th>Akcie</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <StateCell colSpan={5}>Načítavam relácie…</StateCell>
                            </tr>
                        )}
                        {!loading && groups.length === 0 && (
                            <tr>
                                <StateCell colSpan={5}>Žiadne aktívne relácie.</StateCell>
                            </tr>
                        )}
                        {!loading &&
                            groups.map((group) => (
                                <tr key={group.key}>
                                    <td>
                                        <UserCell>
                                            <span>{group.userEmail}</span>
                                            {isMine(group) && <Chip $tone="info">vy</Chip>}
                                        </UserCell>
                                        <Muted>#{group.userId}</Muted>
                                    </td>
                                    <td>{formatSessionTime(group.createdAt)}</td>
                                    <td>{tokenSummary(group)}</td>
                                    <td>
                                        <Chip $tone={group.mfaVerified ? 'ok' : 'warn'}>
                                            {group.mfaVerified ? 'overená' : 'neoverená'}
                                        </Chip>
                                    </td>
                                    <td>{renderActions(group)}</td>
                                </tr>
                            ))}
                    </tbody>
                </Table>
            </TableWrap>

            <Confirm>
                <Label htmlFor="kill-switch">Núdzové odhlásenie všetkých</Label>
                <Hint style={{ margin: '0 0 10px' }}>
                    Ukončí každú reláciu každého používateľa vrátane vašej — všetci sa budú musieť
                    znova prihlásiť. Na potvrdenie napíšte {KILL_PHRASE}.
                </Hint>
                <Row>
                    <Input
                        id="kill-switch"
                        value={killPhrase}
                        onChange={(e) => setKillPhrase(e.target.value)}
                        placeholder={KILL_PHRASE}
                        autoComplete="off"
                        style={{ maxWidth: 180 }}
                    />
                    <Danger type="button" disabled={busy || !killArmed} onClick={killAll}>
                        {busyKey === 'all' ? 'Odhlasujem…' : 'Odhlásiť všetkých'}
                    </Danger>
                </Row>
            </Confirm>
        </Card>
    );
};

/* ---------------- styles ---------------- */

const UserCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    word-break: break-all;
`;

/** Stacked rather than side by side: "Odhlásiť používateľa všade" is long, and two long links on one
 *  line make the wrong one easy to hit. */
const Actions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 7px;
    white-space: nowrap;
`;

export default SessionsCard;
